/**
 * ActionTimeline — Journal de bord chronologique du candidat.
 *
 * Rassemble en UNE seule vue tous les événements qui touchent le candidat :
 *   1. Les changements de statut (candidate.status_history) — validation,
 *      refus, programmation, no-show, mise au vivier, écarté, etc.
 *   2. Les jalons du dossier d'embauche — envoyé, ouvert, soumis, validé,
 *      corrections demandées, relance J+3.
 *   3. Les jalons du contrat — préparé, envoyé, ouvert, signé, annulé,
 *      relance J+2.
 *
 * Tous les événements sont fusionnés puis triés du plus récent au plus
 * ancien pour donner à l'admin / pré-rec / référent un audit complet
 * sans quitter la fiche candidat.
 *
 * Les jalons "dossier" et "contrat" sont chargés au montage via les
 * routes `/api/onboarding/candidates/{cid}` et `/api/contracts/candidates/{cid}` ;
 * ces endpoints ignorent gracieusement le 404 (pas de dossier / pas de
 * contrat encore créé) sans afficher d'erreur.
 */
import { motion } from "framer-motion";
import {
    Briefcase,
    CalendarCheck,
    Check,
    ClipboardCheck,
    FileCheck,
    FileSignature,
    FileText,
    History,
    Hourglass,
    Inbox,
    Mail,
    MailCheck,
    Pause,
    Repeat,
    RotateCcw,
    Send,
    Sparkles,
    ThumbsDown,
    ThumbsUp,
    Trophy,
    UserCheck,
    UserMinus,
    UserX,
    XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { formatDateTime } from "../../lib/format";


// Status → human-readable French label + icon + accent.
const STATUS_META = {
    new: { label: "Nouveau", Icon: Inbox, tone: "bg-slate-100 text-slate-700" },
    prerec_assigned: { label: "Pré-rec assigné", Icon: UserCheck, tone: "bg-slate-100 text-slate-700" },
    prerec_done: { label: "Pré-rec fait", Icon: Check, tone: "bg-emerald-100 text-emerald-800" },
    immersion_scheduled: { label: "Immersion programmée", Icon: CalendarCheck, tone: "bg-amber-100 text-amber-800" },
    immersion_done: { label: "Immersion faite", Icon: Trophy, tone: "bg-sky-100 text-sky-800" },
    awaiting_validation: { label: "En attente de validation", Icon: Hourglass, tone: "bg-amber-100 text-amber-800" },
    validated: { label: "Validé", Icon: ThumbsUp, tone: "bg-emerald-100 text-emerald-800" },
    onboarding: { label: "Onboarding", Icon: Briefcase, tone: "bg-blue-100 text-blue-800" },
    hired: { label: "Embauché", Icon: Sparkles, tone: "bg-emerald-100 text-emerald-800" },
    rejected: { label: "Refusé", Icon: ThumbsDown, tone: "bg-red-100 text-red-800" },
    set_aside: { label: "Mis en retrait", Icon: Pause, tone: "bg-slate-100 text-slate-700" },
    in_talent_pool: { label: "Au vivier", Icon: UserCheck, tone: "bg-purple-100 text-purple-800" },
};


// Reason code → human-readable label (mirrored from backend _REASON_LABELS).
const REASON_LABELS = {
    profil_ideal: "Profil idéal pour la mission",
    bonne_posture_terrain: "Bonne posture terrain",
    validation_directe: "Validation directe sans réserve",
    candidat_indisponible: "Candidat plus disponible",
    criteres_non_remplis: "Critères non remplis",
    immersion_inadequat: "Inadapté au terrain",
    immersion_comportement: "Comportement inadéquat",
    immersion_no_show: "Candidat non présenté",
    immersion_candidat_retire: "Candidat retiré",
    autre: "Autre motif",
    // Onboarding lifecycle — set by the CEO's "Retirer du circuit" + quick-onboarding actions
    removed_from_onboarding: "Retiré du circuit d'embauche",
    quick_onboarding: "Ajout direct en onboarding",
    quick_onboarding_force: "Onboarding forcé (profil existant réutilisé)",
};


// Meta for the synthetic dossier + contract milestones. Each entry gives
// the label, tone, and icon used to render the pill. The `kind` value is
// what we store on the merged event so the renderer can dispatch.
const LIFECYCLE_META = {
    __form_sent__: { label: "Dossier envoyé", Icon: Send, tone: "bg-slate-100 text-slate-700" },
    __form_opened__: { label: "Dossier ouvert", Icon: Mail, tone: "bg-indigo-100 text-indigo-800" },
    __form_submitted__: { label: "Dossier soumis", Icon: ClipboardCheck, tone: "bg-amber-100 text-amber-800" },
    __form_approved__: { label: "Dossier validé", Icon: FileCheck, tone: "bg-emerald-100 text-emerald-800" },
    __form_needs_fix__: { label: "Corrections demandées", Icon: FileText, tone: "bg-rose-100 text-rose-800" },
    __form_reminded__: { label: "Relance dossier (J+3)", Icon: MailCheck, tone: "bg-slate-100 text-slate-700" },
    __contract_ready__: { label: "Contrat préparé", Icon: FileText, tone: "bg-slate-100 text-slate-700" },
    __contract_sent__: { label: "Contrat envoyé", Icon: Send, tone: "bg-indigo-100 text-indigo-800" },
    __contract_opened__: { label: "Contrat ouvert", Icon: Mail, tone: "bg-indigo-100 text-indigo-800" },
    __contract_signed__: { label: "Contrat signé", Icon: FileSignature, tone: "bg-emerald-100 text-emerald-800" },
    __contract_voided__: { label: "Contrat annulé", Icon: XCircle, tone: "bg-red-100 text-red-800" },
    __contract_reminded__: { label: "Relance contrat (J+2)", Icon: MailCheck, tone: "bg-slate-100 text-slate-700" },
    __contract_end_reminded__: { label: "Alerte fin de contrat (J-5)", Icon: MailCheck, tone: "bg-amber-100 text-amber-900" },
    __contract_renewed__: { label: "Renouvellement", Icon: Repeat, tone: "bg-shariti-orange-soft text-shariti-orange" },
};


function StatusBadge({ status }) {
    const meta = STATUS_META[status] || { label: status, Icon: Hourglass, tone: "bg-slate-100 text-slate-600" };
    const { Icon } = meta;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold ${meta.tone}`}>
            <Icon className="h-3 w-3" />
            {meta.label}
        </span>
    );
}


function LifecycleBadge({ kind }) {
    const meta = LIFECYCLE_META[kind];
    if (!meta) return null;
    const { Icon } = meta;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold ${meta.tone}`}>
            <Icon className="h-3 w-3" />
            {meta.label}
        </span>
    );
}


// Extract lifecycle events (dossier + contrats) from the raw API responses.
// `contracts` is an array (initial + all renewals) sorted from oldest to
// newest so v1 → v2 → v3 renders naturally in the timeline. Each contract
// event carries a `version_label` ("v1", "v2"…) that the renderer surfaces
// as a mini badge next to the pill.
function buildLifecycleEvents(onboardingForm, contracts) {
    const evts = [];
    if (onboardingForm) {
        const push = (at, kind, comment) => {
            if (!at) return;
            evts.push({ at, kind, to_status: kind, by_name: "Système", comment: comment || null });
        };
        push(onboardingForm.sent_at, "__form_sent__");
        push(onboardingForm.opened_at, "__form_opened__");
        push(onboardingForm.submitted_at, "__form_submitted__");
        push(onboardingForm.approved_at, "__form_approved__");
        push(onboardingForm.needs_fix_at, "__form_needs_fix__", onboardingForm.needs_fix_note);
        push(onboardingForm.reminder_sent_at, "__form_reminded__");
    }

    const contractList = Array.isArray(contracts) ? contracts : (contracts ? [contracts] : []);
    contractList.forEach((contract) => {
        if (!contract) return;
        // Version label: initial = v1, first renewal = v2, second = v3, …
        // `renewal_index` is 0 for the initial contract, >=1 for renewals.
        const idx = contract.renewal_index || 0;
        const version_label = `v${idx + 1}`;
        const push = (at, kind, extra = {}) => {
            if (!at) return;
            evts.push({
                at,
                kind,
                to_status: kind,
                by_name: "Système",
                comment: null,
                version_label,
                contract_id: contract.id,
                is_renewal: !!contract.is_renewal,
                ...extra,
            });
        };
        push(contract.created_at, "__contract_ready__");
        // Surface renewals as a dedicated pill so they POP visually and
        // build the v1 → v2 → v3 lineage the CEO asked for. Only emit
        // for actual renewals (idx >= 1) — otherwise "__contract_ready__"
        // is enough for the initial contract.
        if (contract.is_renewal && contract.created_at) {
            evts.push({
                at: contract.created_at,
                kind: "__contract_renewed__",
                to_status: "__contract_renewed__",
                by_name: "Système",
                comment: null,
                version_label,
                contract_id: contract.id,
                is_renewal: true,
                parent_contract_id: contract.parent_contract_id || null,
            });
        }
        push(contract.sent_at, "__contract_sent__");
        push(contract.opened_at, "__contract_opened__");
        push(contract.signed_at, "__contract_signed__");
        push(contract.voided_at, "__contract_voided__");
        push(contract.reminder_sent_at, "__contract_reminded__");
        push(contract.end_reminder_sent_at, "__contract_end_reminded__");
    });
    return evts;
}


export default function ActionTimeline({ candidate }) {
    const cid = candidate?.id;
    const [onboardingForm, setOnboardingForm] = useState(null);
    const [contracts, setContracts] = useState([]);

    // Fetch the two lifecycle documents once we have a candidate id. Both
    // 404 gracefully when the dossier / contrat doesn't exist yet.
    useEffect(() => {
        if (!cid) return undefined;
        let alive = true;
        api.get(`/onboarding/candidates/${cid}`)
            .then(({ data }) => { if (alive) setOnboardingForm(data || null); })
            .catch(() => { if (alive) setOnboardingForm(null); });
        // Full contract history (initial + all renewals) — sorted oldest → newest
        // by the backend so v1 → v2 → v3 renders naturally.
        api.get(`/contracts/candidates/${cid}/history`)
            .then(({ data }) => { if (alive) setContracts(Array.isArray(data) ? data : []); })
            .catch(() => { if (alive) setContracts([]); });
        return () => { alive = false; };
    }, [cid]);

    const history = candidate?.status_history || [];

    // Synthetic no-show event — not stored in status_history because no-show
    // is a flag, not a status. We surface it here so the timeline doesn't
    // miss it.
    const extraEvents = [];
    if (candidate?.immersion_no_show && candidate.immersion_no_show_at) {
        extraEvents.push({
            from_status: candidate.status,
            to_status: "__no_show__",
            at: candidate.immersion_no_show_at,
            by_name: candidate.immersion_no_show_by_name,
            by_id: candidate.immersion_no_show_by_id,
        });
    }

    const lifecycleEvents = buildLifecycleEvents(onboardingForm, contracts);
    const events = [...history, ...extraEvents, ...lifecycleEvents]
        .filter((e) => e?.at)
        .sort((a, b) => new Date(b.at) - new Date(a.at));

    if (events.length === 0) {
        return (
            <div className="card-soft p-5" data-testid="action-timeline-empty">
                <div className="flex items-center gap-2 text-sm font-extrabold text-shariti-black">
                    <History className="h-4 w-4 text-shariti-orange" />
                    Journal de bord
                </div>
                <div className="mt-2 text-xs italic text-slate-500">
                    Aucune action enregistrée pour ce candidat.
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-soft p-5 md:p-6"
            data-testid="action-timeline"
        >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-2xl bg-shariti-orange-soft text-shariti-orange inline-flex items-center justify-center">
                        <History className="h-4 w-4" />
                    </div>
                    <div>
                        <div className="text-sm font-extrabold text-shariti-black">
                            Journal de bord
                        </div>
                        <div className="text-[11px] font-medium text-slate-500">
                            Chaque étape, du premier appel à la signature — qui, quand, pourquoi
                        </div>
                    </div>
                </div>
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 bg-shariti-cream px-2 py-1 rounded-full">
                    {events.length} évènement{events.length > 1 ? "s" : ""}
                </span>
            </div>

            {contracts && contracts.length > 1 && (
                <div
                    className="mb-4 rounded-2xl bg-shariti-orange-soft border-2 border-shariti-orange/30 p-3 sm:p-4"
                    data-testid="renewal-chain"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Repeat className="h-4 w-4 text-shariti-orange" />
                        <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange">
                            Chaîne de contrats — {contracts.length} version{contracts.length > 1 ? "s" : ""}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {contracts.map((c, i) => {
                            const label = `v${(c.renewal_index || 0) + 1}`;
                            const isRenewal = !!c.is_renewal;
                            const startDate = (c.params?.start_date || "").slice(0, 10);
                            const endDate = (c.params?.end_date || "").slice(0, 10);
                            const isSigned = c.status === "signed";
                            return (
                                <span key={c.id} className="inline-flex items-center gap-1.5" data-testid={`renewal-chain-node-${label}`}>
                                    {i > 0 && (
                                        <span className="text-shariti-orange font-black text-sm select-none">→</span>
                                    )}
                                    <span
                                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black tracking-wider ${
                                            isRenewal
                                                ? "bg-shariti-orange text-white shadow-sm"
                                                : "bg-white text-shariti-black border-2 border-shariti-orange/40"
                                        }`}
                                    >
                                        {label}
                                        {startDate && endDate && (
                                            <span className="font-medium opacity-80 ml-1">
                                                {startDate.slice(5).replace("-", "/")}–{endDate.slice(5).replace("-", "/")}
                                            </span>
                                        )}
                                        {isSigned && <Check className="h-3 w-3" />}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {events.map((evt, idx) => {
                    const isNoShow = evt.to_status === "__no_show__";
                    const isLifecycle = typeof evt.to_status === "string" && evt.to_status.startsWith("__") && !isNoShow;
                    const isRemoval = evt.reason === "removed_from_onboarding";
                    const reasonLabel = evt.reason ? REASON_LABELS[evt.reason] || evt.reason : null;
                    return (
                        <div
                            key={`${evt.at}-${idx}`}
                            className="flex gap-3 group"
                            data-testid={`timeline-event-${idx}`}
                        >
                            {/* Timeline rail */}
                            <div className="flex flex-col items-center shrink-0">
                                <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${idx === 0 ? "bg-shariti-orange ring-4 ring-shariti-orange-soft" : "bg-slate-300"}`} />
                                {idx !== events.length - 1 && (
                                    <div className="w-px flex-1 bg-shariti-gray mt-1" />
                                )}
                            </div>

                            <div className="flex-1 min-w-0 pb-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {isNoShow ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-amber-100 text-amber-900">
                                            <UserX className="h-3 w-3" /> No-show marqué
                                        </span>
                                    ) : isLifecycle ? (
                                        <>
                                            <LifecycleBadge kind={evt.to_status} />
                                            {evt.version_label && (
                                                <span
                                                    className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-black tracking-wider ${
                                                        evt.is_renewal
                                                            ? "bg-shariti-orange text-white"
                                                            : "bg-slate-200 text-slate-700"
                                                    }`}
                                                    data-testid={`event-version-${evt.version_label}`}
                                                >
                                                    {evt.version_label}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {/* Dedicated red "Retiré du circuit" pill so the
                                                CEO can spot removals at a glance in the
                                                journal, alongside the target status badge
                                                (validé / écarté / refusé). */}
                                            {isRemoval && (
                                                <span
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-red-100 text-red-800"
                                                    title={evt.comment || "Retiré du circuit d'embauche par l'admin"}
                                                    data-testid={`timeline-event-removal-${idx}`}
                                                >
                                                    <UserMinus className="h-3 w-3" />
                                                    Retiré du circuit
                                                </span>
                                            )}
                                            {evt.from_status && (
                                                <>
                                                    <StatusBadge status={evt.from_status} />
                                                    <RotateCcw className="h-3 w-3 text-slate-400" />
                                                </>
                                            )}
                                            {/* When the removal target was 'set_aside', the
                                                backend keeps status='validated' + flips the
                                                boolean flag. Surface it as a proper "Mis en
                                                retrait" badge here so the CEO's intent shows
                                                up faithfully in the timeline. */}
                                            {isRemoval && evt.removal_target === "set_aside" ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-amber-100 text-amber-900">
                                                    <Pause className="h-3 w-3" />
                                                    Mis en retrait
                                                </span>
                                            ) : (
                                                <StatusBadge status={evt.to_status} />
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="mt-1.5 text-[11px] text-slate-600 flex flex-wrap gap-x-2 gap-y-0.5">
                                    <span className="font-extrabold text-shariti-black">{formatDateTime(evt.at)}</span>
                                    <span className="text-slate-400">·</span>
                                    <span>par <strong className="text-shariti-orange">{evt.by_name || "Inconnu"}</strong></span>
                                </div>
                                {(reasonLabel || evt.comment) && (
                                    <div
                                        className="mt-1 text-[11px] text-slate-700 leading-snug bg-shariti-cream rounded-lg px-2.5 py-1.5"
                                        title={evt.comment || undefined}
                                    >
                                        {reasonLabel && (
                                            <div className="font-bold text-slate-800">Motif : {reasonLabel}</div>
                                        )}
                                        {evt.comment && (
                                            <div className="italic text-slate-600 mt-0.5">«&nbsp;{evt.comment}&nbsp;»</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </motion.div>
    );
}
